// frontend/src/pages/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Mail, Lock, User, Eye, EyeOff, Loader, Sparkles } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import api from '../services/api';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, login, signup } = useAppContext();

  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showResendOption, setShowResendOption] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  useEffect(() => {
    if (state.currentUser) navigate('/');
  }, [state.currentUser, navigate]);

  const handleUsernameChange = (val: string) => {
    setUsername(val);
    if (val.length < 3) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    clearTimeout((window as any)._usernameTimer);
    (window as any)._usernameTimer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/check-username?username=${val}`);
        setUsernameStatus(res.data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setShowResendOption(false);

    if (!isLogin && usernameStatus === 'taken') {
      setMessage({ type: 'error', text: 'That username is already taken. Please choose another.' });
      return;
    }

    try {
      const result = isLogin
        ? await login(email, password)
        : await signup(name, username, email, password);

      if (result.success) {
        setMessage({
          type: 'success',
          text: isLogin ? 'Welcome back! 💕' : 'Account created! Check your email to verify. 💌',
        });
        setTimeout(() => navigate('/'), 1500);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
    }
  };

  const handleResendVerification = async () => {
    if (!email) return;
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email });
      setMessage({ type: 'success', text: 'Verification email resent! Check your inbox 💌' });
      setShowResendOption(false);
    } catch {
      setMessage({ type: 'error', text: 'Failed to resend. Please try again.' });
    } finally {
      setResendLoading(false);
    }
  };

  const resetForm = () => {
    setName(''); setUsername(''); setEmail(''); setPassword('');
    setMessage(null); setShowResendOption(false); setUsernameStatus('idle');
  };

  const messageStyles = {
    success: 'bg-green-50 text-green-700 border border-green-200',
    error: 'bg-red-50 text-red-700 border border-red-200',
    warning: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  };

  return (
    <div className="min-h-screen bg-[#faf8f6] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-2">
            <div className="relative w-16 h-16">
  <img src="/logo2.jpeg" alt="RomComThis" className="w-full h-full object-contain" />
  <Heart className="absolute -top-1 -right-1 w-4 h-4 text-romcom-red fill-current" />
</div>
            <div>
              <p className="text-2xl font-playfair font-bold text-romcom-red leading-none">ROMCOMTHIS</p>
              <h1 className="text-xs font-semibold tracking-widest text-gray-400 uppercase">For the Love of RomComs</h1>
            </div>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-[#faf8f6] rounded-3xl shadow-xl p-8 border border-romcom-pink/20">

          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-2xl p-1 mb-8">
            <button
              onClick={() => { setIsLogin(true); resetForm(); }}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all ${
                isLogin ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setIsLogin(false); resetForm(); }}
              className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all ${
                !isLogin ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create Account
            </button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={isLogin ? 'login' : 'register'}
              initial={{ opacity: 0, x: 15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -15 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-2xl font-playfair font-bold text-gray-800 mb-1">
                {isLogin ? 'Welcome back! 💕' : 'Join the club 💕'}
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                {isLogin ? 'Sign in to continue your romcom journey' : 'Create your free account and start tracking'}
              </p>

              {/* Alert */}
              {message && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-xl mb-4 text-sm font-medium ${messageStyles[message.type]}`}
                >
                  {message.text}
                  {showResendOption && (
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading}
                      className="block mt-2 text-xs underline font-semibold disabled:opacity-50"
                    >
                      {resendLoading ? 'Sending...' : 'Resend verification email'}
                    </button>
                  )}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">

                {!isLogin && (
                  <>
                    {/* Display Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Name</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={name}
                          onChange={e => setName(e.target.value)}
                          placeholder="Your full name"
                          required
                          minLength={2}
                          className="w-full pl-11 pr-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent outline-none text-sm"
                        />
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                        <input
                          type="text"
                          value={username}
                          onChange={e => handleUsernameChange(e.target.value)}
                          placeholder="your_username"
                          required
                          minLength={3}
                          pattern="[a-zA-Z0-9_]+"
                          className={`w-full pl-8 pr-10 py-3.5 border rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent outline-none text-sm transition-colors ${
                            usernameStatus === 'taken' ? 'border-red-400 bg-red-50' :
                            usernameStatus === 'available' ? 'border-green-400 bg-green-50' :
                            'border-gray-200'
                          }`}
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          {usernameStatus === 'checking' && <Loader className="w-4 h-4 animate-spin text-gray-400" />}
                          {usernameStatus === 'available' && <span className="text-green-500 font-bold">✓</span>}
                          {usernameStatus === 'taken' && <span className="text-red-500 font-bold">✗</span>}
                        </div>
                      </div>
                      {usernameStatus === 'taken' && (
                        <p className="text-xs text-red-500 mt-1">Username already taken — try another!</p>
                      )}
                      {usernameStatus === 'available' && (
                        <p className="text-xs text-green-500 mt-1">Username is available!</p>
                      )}
                      {usernameStatus === 'idle' && (
                        <p className="text-xs text-gray-400 mt-1">Letters, numbers and underscores only</p>
                      )}
                    </div>
                  </>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full pl-11 pr-4 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={isLogin ? 'Enter your password' : 'Min 8 characters'}
                      required
                      minLength={8}
                      className="w-full pl-11 pr-12 py-3.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {!isLogin && <p className="text-xs text-gray-400 mt-1">At least 8 characters</p>}
                </div>

                {isLogin && (
                  <div className="text-right -mt-1">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-romcom-red hover:underline font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={state.isLoading || (!isLogin && usernameStatus === 'taken')}
                  className="w-full bg-gradient-to-r from-romcom-red to-romcom-pink text-white py-4 rounded-xl font-semibold text-sm hover:opacity-90 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {state.isLoading ? (
                    <><Loader className="w-4 h-4 animate-spin" /> {isLogin ? 'Signing in...' : 'Creating account...'}</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> {isLogin ? 'Sign In' : 'Create Account'}</>
                  )}
                </button>
              </form>

              <p className="text-center text-gray-500 text-sm mt-5">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <button
                  onClick={() => { setIsLogin(!isLogin); resetForm(); }}
                  className="ml-1 text-romcom-red font-semibold hover:underline"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>

              <div className="text-center mt-3">
                <Link to="/" className="text-gray-400 text-xs hover:text-romcom-red transition-colors">
                  ← Back to home
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;